import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface CartLine {
  id: string;
  itemType: "product" | "ticket";
  productId?: string;
  ticketTypeId?: string;
  eventId?: string;
  sellerId?: string | null;
  title: string;
  unitPrice: number;
  quantity: number;
  image?: string | null;
}

interface CartValue {
  lines: CartLine[];
  total: number;
  count: number;
  add: (line: Omit<CartLine, "id">) => void;
  remove: (id: string) => void;
  setQuantity: (id: string, quantity: number) => void;
  clear: () => void;
  checkout: (buyerId: string, buyerEmail?: string | null, only?: Omit<CartLine, "id">[]) => Promise<{ orderId: string; authorizationUrl: string }>;
}

const STORAGE_KEY = "mcg_cart_v1";
const CartContext = createContext<CartValue | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [lines, setLines] = useState<CartLine[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as CartLine[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(lines)); } catch { /* storage unavailable */ }
  }, [lines]);

  const add: CartValue["add"] = useCallback((line) => {
    setLines((prev) => {
      const key = line.productId ?? line.ticketTypeId;
      const match = prev.find((l) => (l.productId ?? l.ticketTypeId) === key);
      if (match) {
        return prev.map((l) => (l.id === match.id ? { ...l, quantity: l.quantity + line.quantity } : l));
      }
      return [...prev, { ...line, id: crypto.randomUUID() }];
    });
  }, []);

  const remove = useCallback((id: string) => setLines((prev) => prev.filter((l) => l.id !== id)), []);
  const setQuantity = useCallback((id: string, quantity: number) => {
    setLines((prev) => prev.flatMap((l) => (l.id === id ? (quantity <= 0 ? [] : [{ ...l, quantity }]) : [l])));
  }, []);
  const clear = useCallback(() => setLines([]), []);

  const total = useMemo(() => lines.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0), [lines]);
  const count = useMemo(() => lines.reduce((sum, l) => sum + l.quantity, 0), [lines]);

  const checkout: CartValue["checkout"] = useCallback(async (buyerId, buyerEmail, only) => {
    const use = only ?? lines;
    const useTotal = use.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0);
    if (!use.length) throw new Error("Your cart is empty.");

    // 1. Record the order as pending — nothing is fulfilled until Paystack confirms payment.
    const { data: order, error } = await supabase
      .from("orders")
      .insert({ buyer_id: buyerId, buyer_email: buyerEmail ?? null, total: useTotal, status: "pending" })
      .select()
      .single();
    if (error) throw error;

    const items = use.map((l) => ({
      order_id: order.id,
      item_type: l.itemType,
      product_id: l.productId ?? null,
      ticket_type_id: l.ticketTypeId ?? null,
      seller_id: l.sellerId ?? null,
      title: l.title,
      quantity: l.quantity,
      unit_price: l.unitPrice,
    }));
    const { error: itemsError } = await supabase.from("order_items").insert(items);
    if (itemsError) throw itemsError;

    // 2. Start a real Paystack transaction and hand back the hosted checkout URL.
    const { data, error: fnError } = await supabase.functions.invoke("paystack-initialize", {
      body: { order_id: order.id, callback_url: `${window.location.origin}/payment/return` },
    });
    if (fnError) throw new Error(fnError.message);
    if (!data?.authorization_url) throw new Error(data?.error ?? "Could not start the payment.");

    return { orderId: order.id, authorizationUrl: data.authorization_url as string };
  }, [lines, total]);

  const value = useMemo(
    () => ({ lines, total, count, add, remove, setQuantity, clear, checkout }),
    [lines, total, count, add, remove, setQuantity, clear, checkout],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
